import { openDB } from 'idb';
import api from '../api/axios';

const DB_NAME = 'cravings-pos-db';
const STORE_NAME = 'offline-orders';

const DB_VERSION = 5; // Bump version to 5 for store alignment
const CACHE_STORE = 'menu-cache';
const ACTIONS_STORE = 'offline-actions';

const getBusinessDate = (date = new Date()) => {
    const d = new Date(date);
    const hour = d.getHours();

    // Business day transitions at 4 PM (16:00)
    // If before 4 PM, it's still the previous business day
    if (hour < 16) {
        d.setDate(d.getDate() - 1);
    }

    // Return YYYY-MM-DD in LOCAL time
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Initialize DB
const initDB = async () => {
    return openDB(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion, newVersion, transaction) {
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
            }
            if (!db.objectStoreNames.contains(CACHE_STORE)) {
                db.createObjectStore(CACHE_STORE);
            }
            if (!db.objectStoreNames.contains(ACTIONS_STORE)) {
                db.createObjectStore(ACTIONS_STORE, { keyPath: 'id', autoIncrement: true });
            }
        },
    });
};

export const SyncManager = {
    // --- Pre-fetching Logic ---
    prefetchCriticalData: async () => {
        if (!navigator.onLine) return;

        console.log('Prefetching critical data...');
        const today = new Date().toISOString().slice(0, 10);
        const startOfMonth = new Date();
        startOfMonth.setDate(1);
        const startStr = startOfMonth.toISOString().slice(0, 10);

        try {
            // These calls populate the Service Worker Cache (NetworkFirst/StaleWhileRevalidate)
            // Critical: Match the exact URLs used in ReportsPage and DashboardPage
            const results = await Promise.all([
                api.get('/reports/daily-sales'), // Generic (Dashboard uses this if no params)
                api.get(`/reports/daily-sales?date=${today}`), // ReportsPage default view
                api.get('/inventory/alerts'),
                api.get('/orders?status=completed'),
                api.get('/orders?status=cancelled'),
                api.get('/orders?status=returned'),
                api.get(`/reports/daily-sales?startDate=${startStr}&endDate=${today}`),
                api.get('/sessions/current') // To get current session ID
            ]);

            // Prefetch Current Session Details (for Business Day Report Details)
            const currentSession = results[7]?.data;
            if (currentSession && currentSession.id) {
                console.log(`Prefetching details for session ${currentSession.id}...`);
                await api.get(`/reports/daily-sales?sessionId=${currentSession.id}`);
            }

            // Prefetch Details for Recent Orders (so clicking them works offline)
            // Combine orders from Completed, Cancelled, Returned
            const recentOrders = [
                ...(results[2].data || []),
                ...(results[3].data || []),
                ...(results[4].data || [])
            ];

            // Limit to last 20 orders to save bandwidth
            const ordersToCache = recentOrders
                .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                .slice(0, 20);

            if (ordersToCache.length > 0) {
                console.log(`Prefetching details for ${ordersToCache.length} recent orders...`);
                await Promise.all(ordersToCache.map(o => api.get(`/orders/${o.id}`)));
            }

            console.log('Critical data prefetched successfully.');
        } catch (error) {
            console.error('Prefetch failed:', error);
        }
    },

    // --- Order Sync Logic ---
    // Save order for later sync
    saveOfflineOrder: async (orderData) => {
        const db = await initDB();
        const offlineOrder = {
            ...orderData,
            createdAt: new Date().toISOString(),
            status: 'pending-sync',
            offlineId: `OFF-${Date.now().toString().slice(-4)}` // Temporary ID
        };
        await db.add(STORE_NAME, offlineOrder);
        return offlineOrder;
    },

    // Get all pending orders
    getPendingOrders: async () => {
        const db = await initDB();
        return db.getAll(STORE_NAME);
    },

    // Delete synced order
    deleteOrder: async (id) => {
        const db = await initDB();
        await db.delete(STORE_NAME, id);
    },

    syncOrders: async () => {
        if (!navigator.onLine) return;

        console.log('Starting order synchronization...');

        // 1. Sync Queued Actions First (e.g. Open Shop)
        const actionResults = await SyncManager.syncActions();

        // 2. Resolve Session ID
        let currentSessionId = null;

        // Check if we just opened a shop
        const openShopResult = actionResults.find(r => r.type === 'OPEN_SHOP' && r.success);
        if (openShopResult && openShopResult.data?.id) {
            currentSessionId = openShopResult.data.id;
        }

        const pendingOrders = await SyncManager.getPendingOrders();
        if (pendingOrders.length === 0) return;

        // If still no session ID and we have pending orders, try to fetch it from server
        if (!currentSessionId && pendingOrders.some(o => !o.sessionId)) {
            try {
                const res = await api.get('/sessions/current');
                if (res.data?.isOpen && res.data.session?.id) {
                    currentSessionId = res.data.session.id;
                }
            } catch (e) {
                console.warn("Failed to fetch current session for sync:", e);
            }
        }

        // 3. Propagate Session ID if found
        if (currentSessionId) {
            await SyncManager.updateOrdersSessionId(currentSessionId);
        }

        // 4. Sync Orders
        console.log(`Syncing ${pendingOrders.length} orders...`);

        for (const order of pendingOrders) {
            try {
                // Refresh order data (in case it was updated by updateOrdersSessionId)
                const db = await initDB();
                const freshOrder = await db.get(STORE_NAME, order.id);

                // Remove offline-specific fields before sending
                // eslint-disable-next-line no-unused-vars
                const { id, createdAt, status, offlineId, branch, ...orderPayload } = freshOrder;

                // Ensure branchId is present
                const branchId = freshOrder.branchId || branch?.id;

                // Ensure businessDate is present (backfill for old stuck orders)
                const businessDate = freshOrder.businessDate || getBusinessDate(new Date(createdAt));

                // Send to server
                await api.post('/orders', {
                    ...orderPayload,
                    branchId, // Specifically send branchId
                    businessDate, // Specifically send businessDate
                    offlineId, // Send offlineId to server
                    status: 'completed' // Ensure it's marked completed on server
                });

                // If successful, remove from local DB
                await SyncManager.deleteOrder(order.id);
                console.log(`Synced order ${offlineId}`);
            } catch (error) {
                const errorMsg = error.response?.data?.message || error.message;
                console.error(`Failed to sync order ${order.offlineId}:`, errorMsg);

                // If 400 (Bad Request) and related to session, don't retry indefinitely
                if (error.response?.status === 400 && (errorMsg.includes('session') || errorMsg.includes('branch'))) {
                    console.warn(`Order ${order.offlineId} has invalid sync parameters. Skipping.`);
                }
            }
        }
    },

    // Update pending orders with a session ID
    updateOrdersSessionId: async (sessionId) => {
        if (!sessionId) return;
        const db = await initDB();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const orders = await tx.store.getAll();

        let count = 0;
        for (const order of orders) {
            if (!order.sessionId) {
                order.sessionId = sessionId;
                await tx.store.put(order);
                count++;
            }
        }
        await tx.done;
        if (count > 0) {
            console.log(`Updated ${count} pending orders with session ID: ${sessionId}`);
        }
    },

    // --- Offline Actions Logic (Open/Close Shop) ---
    saveOfflineAction: async (action) => {
        const db = await initDB();
        const offlineAction = {
            ...action,
            createdAt: new Date().toISOString(),
            status: 'pending',
        };
        await db.add(ACTIONS_STORE, offlineAction);
        console.log('Action saved offline:', action.type);
    },

    syncActions: async () => {
        if (!navigator.onLine) return [];

        const db = await initDB();
        const actions = await db.getAll(ACTIONS_STORE);

        if (actions.length === 0) return [];
        console.log(`Syncing ${actions.length} actions...`);
        const results = [];

        for (const action of actions) {
            try {
                let response;
                if (action.type === 'OPEN_SHOP') {
                    response = await api.post('/sessions/open', action.payload);
                } else if (action.type === 'CLOSE_SHOP') {
                    response = await api.post('/sessions/close', action.payload);
                }

                await db.delete(ACTIONS_STORE, action.id);
                console.log(`Synced action: ${action.type}`);
                results.push({ type: action.type, success: true, data: response?.data });
            } catch (error) {
                console.error(`Failed to sync action ${action.type}:`, error);

                // If 400 (Bad Request), likely already open/closed or invalid state.
                // Discard the action to prevent infinite loop.
                if (error.response && error.response.status === 400) {
                    console.warn(`Discarding invalid/duplicate offline action: ${action.type}`);
                    await db.delete(ACTIONS_STORE, action.id);
                }
                results.push({ type: action.type, success: false, error });
            }
        }
        return results;
    },

    hasPendingActions: async () => {
        const db = await initDB();
        const actions = await db.getAll(ACTIONS_STORE);
        return actions.length > 0;
    },

    // --- Menu Caching Logic ---
    cacheMenu: async (categories, menuItems) => {
        const db = await initDB();
        const tx = db.transaction(CACHE_STORE, 'readwrite');
        await Promise.all([
            tx.store.put(categories, 'categories'),
            tx.store.put(menuItems, 'menuItems'),
            tx.store.put(new Date().toISOString(), 'lastUpdated')
        ]);
        await tx.done;
        console.log('Menu cached for offline use');
    },

    getCachedMenu: async () => {
        const db = await initDB();
        const categories = await db.get(CACHE_STORE, 'categories');
        const menuItems = await db.get(CACHE_STORE, 'menuItems');
        return { categories, menuItems };
    },

    // --- Session Caching Logic ---
    cacheSession: async (sessionStatus) => {
        const db = await initDB();
        await db.put(CACHE_STORE, sessionStatus, 'session');
    },

    getCachedSession: async () => {
        const db = await initDB();
        return db.get(CACHE_STORE, 'session');
    },

    getBusinessDate
};
